import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/PaymentInvoices/helpers'
import paymentInvoicesRepository from '@/repository/generatedRepository/paymentInvoicesRepository'
import TableOptions from '@/models/TableOptions'
import { omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import store from '@/store'
import { WIDGET_REPORTS } from '@/constants/widgetReports'
import moment from 'moment'
import { DATE_TIME_FORM_SHORT_FORMAT_DAY_FIRST } from '@/constants/date'

const state = {
  list: [],
  filters: StorageService.get(storageKeys.PAYMENT_INVOICES_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.PAYMENT_INVOICES_FILTERS, filters)

    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
    })

    const { data } = await paymentInvoicesRepository.list(preparedFilters)

    context.commit('setList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async checkSpendUpdate() {
    const result = await store.dispatch('ReportNotificationModule/loadNotification')
    const reportCacheUpdated = Object.keys(result.reportNotifications).every(
      v => !result.reportNotifications[v],
    )
    await store.dispatch('ReportNotificationModule/updateLocalStorage', {
      reportName: WIDGET_REPORTS.PAYMENT_INVOICE,
      reportCacheState: result.reportNotifications,
      reportBuiltAt: moment().format(DATE_TIME_FORM_SHORT_FORMAT_DAY_FIRST),
      reportCacheUpdated,
      ...(reportCacheUpdated ? { widget2_closed: true } : {}),
    })
  },
  async searchPaymentInvoices(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })
    const { data } = await paymentInvoicesRepository.list(preparedFilters)
    return { products: data.items, pagination: data.pagination }
  },
  async loadItem(context, id) {
    const { data } = await paymentInvoicesRepository.get(id)
    return data
  },
  createItem(context, payload) {
    return paymentInvoicesRepository.post(payload)
  },
  loadPreview(context, payload) {
    return paymentInvoicesRepository.preview(payload)
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return paymentInvoicesRepository.put(id, data)
  },
  deleteItem(context, payload) {
    return paymentInvoicesRepository.delete(payload)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.PAYMENT_INVOICES_FILTERS)
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.PAYMENT_INVOICES_FILTERS, updatedFilters)
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
